import {
  FC,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledSubMenu,
  StyledLink,
  SubMenuContent,
  SubmenuContainer,
  SubmenuLink,
  StyledFlex,
  StyledContainer,
} from "./SubMenu.styled";
import { Locale } from "lib/Intl";
import Link from "next/link";
import { Overlay } from "src/molecules/Overlay";
import { AlgoliaSearch } from "src/organisms/Search";
import { closeSearch, getIsOpenSearch } from "./duck";
import { SearchBar } from "src/molecules/SearchBar";
import { NavMenuSkeleton } from "src/organisms/Header/NavMenuSkeleton";
import { getI18nRoute } from "src/utils/i18nRoutes";
import NavigationContext from "src/contexts/navigation.context";
import { useIsScrollAtPosition } from "src/hooks/useIsScrollAtPosition";
import { convertToHyphenated, sanitizeString } from "../ProductOrder/utils";
import { SearchResultDisplayedProps, useSegment } from "src/hooks/useSegment";
import { createReducer, type ReducerAction } from "src/utils/create-reducer";
import { useRouter } from "next/router";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useNavigationItems } from "src/hooks/useNavigationItems";
import { NavigationItems } from "src/types/graphql.d";
import { logger } from "src/utils/helper/logger";

interface Props {
  locale: Locale;
  genderId: string;
  searchAnimationTime: number;
  gendersRef?: RefObject<HTMLDivElement>;
}

type AbandonedTrackerState = {
  hasUserInteraction: boolean;
  data: Partial<SearchResultDisplayedProps>;
};

export type AbandonedTrackerActions =
  | ReducerAction<"SET_USER_INTERACTED", boolean>
  | ReducerAction<"SET_DATA", Partial<SearchResultDisplayedProps>>;

const abandonedTrackerReducer = createReducer<
  AbandonedTrackerState,
  AbandonedTrackerActions
>({
  SET_DATA: (state, payload) => ({
    ...state,
    data: payload,
  }),
  SET_USER_INTERACTED: (state, payload) => ({
    ...state,
    hasUserInteraction: payload,
  }),
});

const abandonedTrackerInitialState: AbandonedTrackerState = {
  data: {},
  hasUserInteraction: false,
};

const SubMenu: FC<Props> = ({ locale, gendersRef, searchAnimationTime }) => {
  const router = useRouter();
  const searchBarContainerRef = useRef<HTMLDivElement>(null);
  const { navigationItems } = useContext(NavigationContext);
  const dispatch = useDispatch();
  const [searchTerm, setsearchTerm] = useState("");
  const [abandonedTrackerState, dispatchAbandonedTracker] = useReducer(
    abandonedTrackerReducer,
    abandonedTrackerInitialState
  );
  const { searchAbandoned } = useSegment();
  const { currentShopType } = useShopTypeContext();
  const { refetch, loading: clientNavigationLoading } = useNavigationItems(
    localeFix({ gender: currentShopType }),
    true
  );
  const [clientNavigation, setClientNavigation] = useState<
    NavigationItems | undefined
  >();

  const showSearchOverlay = useIsScrollAtPosition({
    scrollDirection: "vertical",
    validator: (scrollPos) => scrollPos > 48,
  });

  const isOpenSearch = useSelector(getIsOpenSearch);

  const handleCloseSearch = useCallback(
    (callback?: () => void) => {
      dispatch(closeSearch());

      if (callback && typeof callback === "function") {
        callback();
      }
    },
    [dispatch]
  );

  const triggerCloseSearch = useCallback(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.click();
    }
  }, []);

  const [isSearchOverlayOpened, setIsSearchOverlayOpened] =
    useState(isOpenSearch);

  const handleCloseSearchOverlay = useCallback(() => {
    setIsSearchOverlayOpened(false);
    setsearchTerm("");

    if (!abandonedTrackerState.hasUserInteraction) {
      void searchAbandoned(abandonedTrackerState.data);
    }
  }, [abandonedTrackerState, searchAbandoned]);

  useEffect(() => {
    router.events.on("routeChangeStart", handleCloseSearch);
    return () => {
      router.events.off("routeChangeStart", handleCloseSearch);
    };
  }, [handleCloseSearch, router.events]);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputSubMenuRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const isLinkActive = (url: string, name: string): boolean => {
    if (name === "hot deals" && router.asPath.includes("hot-deals")) {
      return true;
    }
    return router.asPath.startsWith(url);
  };

  const topLevelCategories = useMemo(
    () =>
      navigationItems?.top_level_categories?.slice(0, 4) ||
      clientNavigation?.top_level_categories?.slice(0, 4) ||
      [],
    [navigationItems?.top_level_categories, clientNavigation]
  );

  const navigationLinks = useMemo(() => {
    return (
      navigationItems?.navigation_links
        ?.filter((link) => link.show_on_top)
        .slice(0, 5) ||
      clientNavigation?.navigation_links
        ?.filter((link) => link.show_on_top)
        .slice(0, 5) ||
      []
    );
  }, [navigationItems?.navigation_links, clientNavigation]);

  useEffect(() => {
    if (navigationItems?.navigation_links.length) {
      return;
    }

    refetch({
      shop_type: localeFix({ gender: currentShopType }),
    })
      .then(({ data }) => {
        setClientNavigation(data.navigationItems || {});
      })
      .catch((error) =>
        logger({
          message: "Error fetching navigation items",
          error: error as Error,
        })
      );
  }, [navigationItems?.navigation_links, refetch, currentShopType]);

  useEffect(() => {
    if (isOpenSearch && !isSearchOverlayOpened) {
      setIsSearchOverlayOpened(true);
    }
  }, [isOpenSearch, isSearchOverlayOpened]);

  const ref = useRef(false);

  useEffect(() => {
    if (!ref.current) {
      ref.current = true;
    }
  }, []);

  return (
    <>
      <StyledSubMenu isOpenSearch={isSearchOverlayOpened}>
        <SubMenuContent as="nav" aria-labelledby="categories-select-label">
          <p id="categories-select-label" className="sr-only">
            Categories
          </p>
          <SubmenuContainer
            as="ul"
            data-testid="submenu-items"
            moveLeft={isOpenSearch}
            duration={searchAnimationTime}
          >
            {(!ref.current && !navigationLinks.length) ||
            clientNavigationLoading ? (
              <NavMenuSkeleton />
            ) : (
              navigationLinks
                .map((item, index) => {
                  return (
                    <StyledLink
                      key={`submenu-${index}`}
                      hideOnDesktop={false}
                      hideOnMobile={false}
                    >
                      {item.link && item.title && (
                        <Link href={item.link} as={item.link} prefetch={false}>
                          <SubmenuLink
                            as="span"
                            color={item.colour}
                            active={isLinkActive(item.link, item.title)}
                            data-testid={`menu-item-${convertToHyphenated(
                              item.title
                            )}`}
                          >
                            {sanitizeString(item.title)}
                          </SubmenuLink>
                        </Link>
                      )}
                    </StyledLink>
                  );
                })
                .concat(
                  topLevelCategories.map((item, index) => {
                    const link = `${getI18nRoute({
                      route: `/${
                        localeFix({ gender: currentShopType }) || "women"
                      }`,
                      locale,
                    })}/${item.slug}`;
                    return (
                      <StyledLink
                        key={`submenu-category-${index}`}
                        hideOnDesktop={false}
                        hideOnMobile={false}
                      >
                        {item.name && (
                          <Link href={link} passHref legacyBehavior>
                            <SubmenuLink
                              color={item.colour}
                              as="a"
                              active={isLinkActive(link, item.name)}
                              data-testid={`menu-item-${convertToHyphenated(
                                item.name
                              )}`}
                            >
                              {sanitizeString(item.name)}
                            </SubmenuLink>
                          </Link>
                        )}
                      </StyledLink>
                    );
                  })
                )
            )}
          </SubmenuContainer>
          {!showSearchOverlay ? (
            <SearchBar
              isOpenSearch={isOpenSearch}
              searchAnimationTime={searchAnimationTime}
              searchTerm={searchTerm}
              ref={inputSubMenuRef}
              setSearchTerm={setsearchTerm}
              handleCloseSearch={handleCloseSearch}
              closeButtonRef={closeButtonRef}
            />
          ) : null}
        </SubMenuContent>
      </StyledSubMenu>
      {showSearchOverlay && isSearchOverlayOpened ? (
        <StyledFlex ref={searchBarContainerRef}>
          <StyledContainer>
            <SearchBar
              isOpenSearch={isOpenSearch}
              searchAnimationTime={searchAnimationTime}
              searchTerm={searchTerm}
              ref={inputRef}
              setSearchTerm={setsearchTerm}
              handleCloseSearch={handleCloseSearch}
              closeButtonRef={closeButtonRef}
            />
          </StyledContainer>
        </StyledFlex>
      ) : null}
      <Overlay
        isOpen={isSearchOverlayOpened}
        isActive={isOpenSearch}
        onClose={triggerCloseSearch}
        excludeFromOutside={[
          gendersRef as RefObject<HTMLDivElement>,
          inputRef,
          inputSubMenuRef,
          closeButtonRef,
          searchBarContainerRef,
        ]}
        mobileTop={110}
        tabletTop={173 - (showSearchOverlay ? 48 : 0)}
        delay={searchAnimationTime}
      >
        <AlgoliaSearch
          onClose={triggerCloseSearch}
          onCloseOverlay={handleCloseSearchOverlay}
          isSearchOpened={isOpenSearch}
          searchTerm={searchTerm}
          gender={currentShopType}
          animationTime={searchAnimationTime}
          dispatchAbandonedTracker={dispatchAbandonedTracker}
        />
      </Overlay>
    </>
  );
};

export { SubMenu };
